import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import { People as PeopleIcon } from '@mui/icons-material';
import PortalAccessSection from '../../components/clients/PortalAccessSection';
import InternalOrgUsersDialog from '../../components/InternalOrgUsersDialog';
import { portalFeaturesService } from '../../services/portal/portalFeatures.service';
import { resolveAll, featureLabel } from '../../services/portal/portalFeatures';
import { serverMessage } from '../../utils/apiConvert';

/**
 * Client › Portal tab: what this client's portal shows (Default / On / Off per feature) and who can
 * sign in. 14-portal/PORTAL-EMPLOYEES-AND-FEATURES.md §4.2.
 */
const ClientPortalTab = ({ org, orgId, canWrite, showThaklisFeatures }) => {
  const [defaults, setDefaults] = useState({});
  const [overrides, setOverrides] = useState({});
  const [saved, setSaved] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [usersOpen, setUsersOpen] = useState(false);

  // Success notes close on their own; warnings and errors stay until closed.
  useEffect(() => {
    if (message?.severity !== 'success') return undefined;
    const t = setTimeout(() => setMessage(null), 4000);
    return () => clearTimeout(t);
  }, [message]);

  useEffect(() => {
    let active = true;
    const current = portalFeaturesService.overridesFor({ ...(org || {}), id: orgId });
    setOverrides(current);
    setSaved(current);
    portalFeaturesService.getDefaults()
      .then((d) => { if (active) setDefaults(d || {}); })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [org, orgId]);

  const dirty = JSON.stringify(overrides) !== JSON.stringify(saved);
  const effective = resolveAll(overrides, defaults);
  const offList = Object.entries(effective)
    .filter(([key, on]) => !on && (showThaklisFeatures || !['EMPLOYEES', 'DOC_EXPIRY'].includes(key)))
    .map(([key]) => featureLabel(key));

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const result = await portalFeaturesService.saveOverrides(orgId, overrides);
      setOverrides(result || {});
      setSaved(result || {});
      setMessage({ severity: 'success', text: 'Saved. The client sees the change the next time their portal loads.' });
    } catch (err) {
      setMessage({ severity: 'error', text: serverMessage(err, 'Failed to save portal access') });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={24} /></Box>;
  }

  return (
    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 2fr) minmax(0, 1fr)' }, alignItems: 'start' }}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>Portal access</Typography>
        </Box>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 1.5 }}>
          Default follows Settings › Client Portal. Choose On or Off to set a feature for this client only.
        </Typography>
        {message && <Alert severity={message.severity} sx={{ mb: 1.5 }} onClose={() => setMessage(null)}>{message.text}</Alert>}
        <PortalAccessSection
          hideTitle
          overrides={overrides}
          onChange={setOverrides}
          defaults={defaults}
          showThaklisFeatures={showThaklisFeatures}
          disabled={!canWrite || saving}
        />
        {canWrite && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1.5 }}>
            <Button size="small" disabled={!dirty || saving} onClick={() => setOverrides(saved)} sx={{ textTransform: 'none' }}>Reset</Button>
            <Button size="small" variant="contained" disabled={!dirty || saving} onClick={save} sx={{ textTransform: 'none' }}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Box>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, mb: 0.5 }}>Portal users</Typography>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 1.5 }}>
          People at this client who can sign in, their role and password.
        </Typography>
        <Button size="small" variant="contained" startIcon={<PeopleIcon />} onClick={() => setUsersOpen(true)} sx={{ textTransform: 'none' }}>
          Manage portal users
        </Button>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, mt: 2.5, mb: 0.5 }}>What the client sees</Typography>
        <Typography sx={{ fontSize: '0.8125rem', color: offList.length ? 'text.primary' : 'text.secondary' }}>
          {offList.length ? `Turned off: ${offList.join(', ')}` : 'Every portal feature is on.'}
        </Typography>
      </Paper>

      <InternalOrgUsersDialog open={usersOpen} onClose={() => setUsersOpen(false)} organization={{ ...(org || {}), id: orgId }} />
    </Box>
  );
};

export default ClientPortalTab;
